import axios, {
  type AxiosInstance,
  type AxiosRequestConfig,
  type AxiosResponse,
  type InternalAxiosRequestConfig,
} from 'axios'
import {
  AUTH_AUTHENTICATOR_KEY,
  AUTH_AUTHENTICATOR_HEADER,
  AUTH_SSO_TOKEN_KEY,
} from '@/constants/auth-storage'
import { persistAuthTokenRaw } from '@/stores/auth-utils'
import { createHttpResponseErrorHandler, toHttpError } from './interceptors'

export type { HttpRequestConfig } from './types'
export { HttpError } from './types'
export {
  createHttpResponseErrorHandler,
  extractHttpErrorMessage,
  toHttpError,
} from './interceptors'
export {
  notifyHttpSessionExpired,
  setHttpSessionExpiredHandler,
} from './session'

export type HttpAuthTokenResolver = () => string | null | undefined

let authTokenResolver: HttpAuthTokenResolver | undefined

/** 在应用启动时注册 token 获取方式（可选） */
export function setHttpAuthTokenResolver(resolver: HttpAuthTokenResolver | undefined): void {
  authTokenResolver = resolver
}

function attachAuth(config: InternalAxiosRequestConfig): InternalAxiosRequestConfig {
  const raw = authTokenResolver?.()
  if (!raw) return config
  const value = /^bearer\s+/i.test(raw) ? raw : `Bearer ${raw}`
  config.headers.set('Authorization', value)

  const authenticator = hasExternalAuthSession() ? resolveExternalAuthenticator() : ''
  if (authenticator) {
    config.headers.set(AUTH_AUTHENTICATOR_HEADER, authenticator)
  }
  return config
}

function hasExternalAuthSession(): boolean {
  return (
    String(localStorage.getItem(AUTH_AUTHENTICATOR_KEY) ?? '').trim().length > 0 ||
    String(localStorage.getItem(AUTH_SSO_TOKEN_KEY) ?? '').trim().length > 0
  )
}

function resolveExternalAuthenticator(): string {
  return (
    String(localStorage.getItem(AUTH_AUTHENTICATOR_KEY) ?? '').trim() ||
    String(import.meta.env.VITE_AUTH_AUTHENTICATOR ?? 'my-external-auth').trim()
  )
}

function readResponseHeader(res: AxiosResponse, name: string): string {
  const headers = res.headers as Record<string, unknown> & {
    get?: (key: string) => unknown
  }
  const byGetter = headers.get?.(name) ?? headers.get?.(name.toLowerCase())
  const raw = byGetter ?? headers[name] ?? headers[name.toLowerCase()]
  if (Array.isArray(raw)) return String(raw[0] ?? '').trim()
  return String(raw ?? '').trim()
}

function persistRenewedToken(res: AxiosResponse): void {
  const next = readResponseHeader(res, 'x-new-token')
  if (next) {
    persistAuthTokenRaw(next)
  }
}

function createAxiosInstance(): AxiosInstance {
  const baseURL = String(import.meta.env.VITE_API_BASE_URL ?? '').replace(/\/+$/, '')
  const instance = axios.create({
    baseURL,
    timeout: 30_000,
    headers: {
      'Content-Type': 'application/json',
    },
  })

  instance.interceptors.request.use(
    (config: InternalAxiosRequestConfig) => attachAuth(config),
    (err: unknown) => Promise.reject(toHttpError(err)),
  )

  instance.interceptors.response.use(
    (res: AxiosResponse) => {
      persistRenewedToken(res)
      return res.data
    },
    createHttpResponseErrorHandler(),
  )

  return instance
}

const axiosInstance = createAxiosInstance()

/**
 * 带拦截器的 axios 实例（成功时 resolve 为 `response.data`，与 {@link http} 一致）
 */
export { axiosInstance }

/**
 * 类型安全的 HTTP 封装：成功时返回响应体 `T`（已解包 `data`）
 */
export const http = {
  get<T, D = unknown>(url: string, config?: AxiosRequestConfig<D>): Promise<T> {
    return axiosInstance.get<T, T, D>(url, config)
  },

  delete<T, D = unknown>(url: string, config?: AxiosRequestConfig<D>): Promise<T> {
    return axiosInstance.delete<T, T, D>(url, config)
  },

  head<T, D = unknown>(url: string, config?: AxiosRequestConfig<D>): Promise<T> {
    return axiosInstance.head<T, T, D>(url, config)
  },

  options<T, D = unknown>(url: string, config?: AxiosRequestConfig<D>): Promise<T> {
    return axiosInstance.options<T, T, D>(url, config)
  },

  post<T, B = unknown>(url: string, data?: B, config?: AxiosRequestConfig<B>): Promise<T> {
    return axiosInstance.post<T, T, B>(url, data, config)
  },

  put<T, B = unknown>(url: string, data?: B, config?: AxiosRequestConfig<B>): Promise<T> {
    return axiosInstance.put<T, T, B>(url, data, config)
  },

  patch<T, B = unknown>(url: string, data?: B, config?: AxiosRequestConfig<B>): Promise<T> {
    return axiosInstance.patch<T, T, B>(url, data, config)
  },
}
